import { FeatureLabel } from '@/components/marketing/info-tip';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  IconCheck, IconX, IconMinus, IconSearch, IconCoin, IconSparkles, IconMessages, IconApi, IconWorld,
} from '@tabler/icons-react';
import { ALL_COMPETITORS } from '@/routes/compare/data';
import noindexConfig from '@/lib/noindex.json';

/** Only the comparisons that stay indexed are listed; the rest remain live by URL (see lib/noindex.json). */
const INDEXED_COMPETITORS = ALL_COMPETITORS.filter((c) => noindexConfig.compareIndexed.includes(c.slug));
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildItemListSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import { ArtPanel } from '@/components/marketing/art-panel';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { PageHero } from '@/components/marketing/warm/page-hero';
import { CtaBand } from '@/components/marketing/warm/cta-band';
import { DataTable } from '@/components/marketing/warm/data-table';
import { BAND, Chip, Eyebrow, H2, IconTile, Lead, Muted, RevealSection } from '@/components/marketing/warm/primitives';

// Quick 5-product comparison for the index page
const QUICK_COMPARE: { label: string; values: (boolean | string)[] }[] = [
  { label: 'Free to start', values: ['1,100 free credits', '14-day trial', '3 agents', 'No (14-day trial)', '3 agents'] },
  { label: 'Pricing model', values: ['Pay per ticket: $0.10', 'From $19/agent/mo', 'From $7/agent/mo', 'From $19/agent/mo', 'From $22.50/agent/mo'] },
  { label: 'AI ticket structuring', values: ['Free, no add-on fee', 'Add-on $29/agent', 'Enterprise $40/agent', 'Add-on $50/agent', 'Standard+ included'] },
  { label: 'AI Copilot & Agent', values: ['Roadmap (not yet)', 'Add-on $29/agent', 'Enterprise+', 'Add-on $50/agent', 'Standard+ included'] },
  { label: 'AI Analyst', values: ['1 credit per answer', 'No', 'No', 'No', 'No'] },
  { label: 'Public API (20+ endpoints)', values: ['Every workspace', 'Growth+', 'Standard+', 'All plans', 'All plans'] },
  { label: 'Embedded sessions', values: [true, false, false, false, false] },
  { label: 'Omnichannel', values: ['Coming soon', true, true, true, '~'] },
  { label: 'Knowledge base', values: [true, true, true, true, true] },
  { label: 'SLA tracking', values: [true, true, true, true, true] },
  { label: 'Marketplace apps', values: ['Coming soon', '1000+', '~', '1500+', '1000+'] },
  { label: 'EU data residency', values: [true, '~', '~', '~', 'Premium+'] },
];

const LOOK_FOR = [
  {
    icon: IconCoin,
    title: 'Pricing model',
    body: 'Per-seat pricing scales linearly with headcount. Flat pricing stays the same whether you have 5 or 50 agents. Calculate your cost at your expected team size, not the entry tier.',
  },
  {
    icon: IconSparkles,
    title: 'AI included vs add-on',
    body: 'Many help desks charge extra for AI — per agent, per session, or per resolution. Check what\'s included in the base plan and what triggers an add-on fee.',
  },
  {
    icon: IconMessages,
    title: 'Channels you actually use',
    body: 'Omnichannel sounds great until you\'re paying for phone support you don\'t use. Match the channels to your actual customer behavior.',
  },
  {
    icon: IconApi,
    title: 'API access',
    body: 'If you\'ll integrate with your own product or workflow, check which plans include API access and what the rate limits are. Some tools gate the API behind higher tiers.',
  },
  {
    icon: IconWorld,
    title: 'Data residency',
    body: 'If GDPR or data sovereignty matters, verify where your data is actually stored — not just where the company is headquartered.',
  },
];

function QuickCell({ value }: { value: boolean | string }) {
  if (value === true) return <IconCheck size={18} stroke={2} style={{ color: 'var(--chip-green)' }} aria-label="Included" />;
  if (value === false) return <IconX size={18} stroke={1.75} style={{ color: 'var(--warm-faint)' }} aria-label="Not available" />;
  if (value === '~') return <IconMinus size={18} stroke={2} style={{ color: 'var(--chip-amber)' }} aria-label="Partial or limited" />;
  return <span className="text-xs">{value}</span>;
}

function seatPrice(c: (typeof ALL_COMPETITORS)[number]) {
  const p = c.plans.find((plan) => plan.highlight)?.price || c.plans[1]?.price || 0;
  return p > 0 ? `$${p}/agent/mo` : c.plans.some((plan) => plan.price === 0) ? 'Free plan' : 'Contact sales';
}

export function CompareIndexPage() {
  useEffect(() => {
    const title = 'Kora vs the competition — Compare issue tracking tools';
    const description = 'Compare Kora vs Freshdesk, Zoho Desk, Zendesk, and Jira Service Management. Honest pricing, features, and fit analysis for modern ops teams.';
    setDocumentMeta(title, description);
    setCanonical('/compare');
    setOgImage('/assets/og-compare.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/compare', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'Compare', url: 'https://usekora.app/compare' },
      ]),
      buildItemListSchema(
        INDEXED_COMPETITORS.map((c) => ({
          name: `Kora vs ${c.name}`,
          url: `https://usekora.app/compare/${c.slug}`,
          description: c.tagline,
        })),
      ),
    ]);

    return cleanup;
  }, []);

  const competitors = INDEXED_COMPETITORS;
  // Only show the original 4 in the quick comparison table
  const quickCompareCompetitors = competitors.slice(0, 4);
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? competitors.filter((c) => `${c.name} ${c.tagline}`.toLowerCase().includes(q)) : competitors;
  }, [competitors, query]);

  return (
    <MarketingShell>
      <PageHero
        eyebrow="Compare"
        title={<>Kora vs <br /><Muted><span className="italic">the field</span></Muted></>}
        lead="Honest, side-by-side comparisons. No tool wins everything — find the one that fits your team."
        aside={
          <ArtPanel tint="analyst" className="p-6 md:p-10">
            <div className="max-w-sm mx-auto rounded-2xl p-5" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)', boxShadow: '0 20px 40px -28px rgba(28,26,21,0.35)' }}>
              <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--warm-muted)' }}>Price model</p>
              <ul className="space-y-2.5">
                {quickCompareCompetitors.map((c) => (
                  <li key={c.slug} className="flex items-center justify-between text-sm">
                    <span style={{ color: 'var(--warm-text)' }}>{c.name}</span>
                    <span style={{ color: 'var(--warm-muted)' }}>{seatPrice(c)}</span>
                  </li>
                ))}
                <li className="flex items-center justify-between text-sm pt-2.5" style={{ borderTop: '1px solid var(--warm-border)' }}>
                  <span className="font-medium" style={{ color: 'var(--warm-text)' }}>Kora</span>
                  <span className="font-medium" style={{ color: 'var(--warm-text)' }}>$0.10 per ticket</span>
                </li>
              </ul>
            </div>
          </ArtPanel>
        }
      />

      <RevealSection style={BAND}>
        <Eyebrow>At a glance</Eyebrow>
        <H2>Top 5, <br /><Muted>at a glance</Muted></H2>
        <Lead>The top 10 things teams compare. For the full breakdown, open each comparison page.</Lead>
        <div className="mt-10">
          <DataTable
            caption="Kora compared with four popular helpdesks"
            head={['Feature', 'Kora', ...quickCompareCompetitors.map((c) => c.name)]}
            rows={QUICK_COMPARE.map((row) => [
              <FeatureLabel key="f" label={row.label} />,
              <span key="kora" className="font-medium" style={{ color: 'var(--warm-text)' }}><QuickCell value={row.values[0]} /></span>,
              ...quickCompareCompetitors.map((c, idx) => <QuickCell key={c.slug} value={row.values[idx + 1]} />),
            ])}
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs" style={{ color: 'var(--warm-muted)' }}>
          <span className="flex items-center gap-1.5"><IconCheck size={14} stroke={2} style={{ color: 'var(--chip-green)' }} /> Included</span>
          <span className="flex items-center gap-1.5"><IconMinus size={14} stroke={2} style={{ color: 'var(--chip-amber)' }} /> Partial / limited</span>
          <span className="flex items-center gap-1.5"><IconX size={14} stroke={1.75} style={{ color: 'var(--warm-faint)' }} /> Not available</span>
        </div>
      </RevealSection>

      <RevealSection>
        <Eyebrow>Buying guide</Eyebrow>
        <H2>What to <br /><Muted>look for</Muted></H2>
        <ul className="reveal-stagger mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LOOK_FOR.map((item) => (
            <li key={item.title} className="warm-card p-5" style={{ border: '1px solid var(--warm-border)' }}>
              <IconTile icon={item.icon} />
              <p className="mt-4 text-sm font-semibold" style={{ color: 'var(--warm-text)' }}>{item.title}</p>
              <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>{item.body}</p>
            </li>
          ))}
        </ul>
      </RevealSection>

      <RevealSection style={BAND} id="all">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Eyebrow>All comparisons</Eyebrow>
            <H2>Browse all <br /><Muted>comparisons</Muted></H2>
            <Lead>{competitors.length} tools compared. Find the one that fits your team.</Lead>
          </div>
          <label className="relative block md:w-80">
            <span className="sr-only">Search comparisons</span>
            <IconSearch size={16} stroke={1.75} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--warm-muted)' }} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools, e.g. Zendesk"
              className="w-full rounded-xl pl-10 pr-4 py-3 text-sm outline-none focus:ring-2"
              style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)', '--tw-ring-color': 'var(--warm-text)' } as React.CSSProperties}
            />
          </label>
        </div>

        <p className="mt-6 text-xs" aria-live="polite" style={{ color: 'var(--warm-muted)' }}>
          {query ? `${visible.length} of ${competitors.length} tools` : ' '}
        </p>

        {visible.length === 0 ? (
          <div className="mt-4 rounded-2xl p-10 text-center" style={{ background: 'var(--warm-page)', border: '1px dashed var(--warm-border)' }}>
            <p className="text-sm" style={{ color: 'var(--warm-text)' }}>No match for "{query}".</p>
            <button onClick={() => setQuery('')} className="mt-3 text-sm underline" style={{ color: 'var(--warm-muted)' }}>See all {competitors.length} comparisons</button>
          </div>
        ) : (
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map((c) => (
              <li key={c.slug} className="min-w-0">
                <Link
                  to={`/compare/${c.slug}`}
                  className="group h-full flex flex-col rounded-2xl p-5 transition-transform hover:-translate-y-0.5"
                  style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)' }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-9 h-9 rounded-[10px] shrink-0 inline-flex items-center justify-center font-display text-lg" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}>{c.name[0]}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: 'var(--warm-text)' }}>Kora vs {c.name}</p>
                        <p className="text-xs truncate" style={{ color: 'var(--warm-muted)' }}>{c.tagline}</p>
                      </div>
                    </div>
                    <ArrowRight className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: 'var(--warm-muted)' }} />
                  </div>
                  <p className="mt-4 text-sm leading-relaxed flex-1" style={{ color: 'var(--warm-muted)' }}>{c.verdictKora}</p>
                  <div className="mt-4 pt-4 flex flex-wrap items-center justify-between gap-2 text-xs" style={{ borderTop: '1px solid var(--warm-border)' }}>
                    <span style={{ color: 'var(--warm-muted)' }}>{c.name}: {seatPrice(c)}</span>
                    <Chip tone="dark">Kora: $0.10 per ticket</Chip>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </RevealSection>

      <RevealSection>
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <p className="text-sm text-zinc-600">
            Comparing AI pricing?{' '}
            <Link to="/ai-helpdesk-pay-per-resolution" className="underline">Kora AI: $0.10 per draft, $0.30 per confirmed resolution</Link>
            {' · '}
            <Link to="/ai-reply-drafting-software" className="underline">AI reply drafting</Link>
            {' · '}
            <Link to="/ai-customer-support-agent" className="underline">AI customer support agent</Link>
          </p>
        </div>
      </RevealSection>

      <CtaBand
        title={<>See for <br /><Muted>yourself</Muted></>}
        lead="Start free, invite your team, paste your first ticket. No credit card, no per-seat pricing, no setup call required."
      />
    </MarketingShell>
  );
}
